import Vue from "vue";
import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {
    devirHatasiniNormallestir,
    KimlikDevriNedeni, KimlikDevriSonucu, KimlikDevriYoneticisi,
    nedenSec, parmakIziOlustur
} from "@/utils/kimlikDevri";

/**
 * İmzacı (e-imza uygulaması) bağlantısı ve kimlik devri durumu (DEV-3899).
 *
 * Tarayıcı artık imzacıyla yoklaşmıyor: yalnız kimliğini (token + büro + TCKN)
 * kabuğa iletir (`setApiCookiesToImza`). "İmzacı bağlı" = socket bağlı VE son devir
 * başarılı. UYAP oturumu ve kart durumu bu modülün konusu değil: UYAP bağlantısı
 * socket 'uyapconnection' olayıyla (store/index.ts `uyapConnection`) gelir. Ancak socket
 * koptuğunda imzacı (kabuk) yok demektir: imzacıdan gelen durumun tamamı (UYAP bayrağı,
 * bot kimliği, imza sahibi avukat ve bakiyeler) `imzaSocketDurumu(false)` ile sıfırlanır;
 * yeniden bağlanınca kabuk bunları yeniden yayınlar (socketmanager.js 'connection').
 *
 * Namespaced DEĞİL: getter/action adları (isImzaConnected, kimlikDevret,
 * connectToImza) eskiden kök store'daydı, çağıranlar aynen kalsın.
 */

export interface KimlikDevriDurumu {
    ok: boolean;
    eskiKabuk: boolean;
    kabukSurumu: string | null;
    sebep: string | null;
    neden: KimlikDevriNedeni | null;
    zaman: number;
}

/** İmzacının şu an adına çalıştığı kimlik (socket 'botKimligi'). TCKN içermez. */
export interface BotKimligi {
    tenantId: string;
    buro: string;
    avukat: string;
    bekleyenGecis: boolean;
}

/** GET /api/v1/imzaci/kimlik cevabı: büronun eİcraPro lisansı ve lisans sahibinin TCKN durumu. */
export interface ImzaLisansDurumu {
    tenantId: string;
    lisansVar: boolean;
    tcknEksik: boolean;
}

export interface ImzaUyarisi {
    anahtar: string;
    tip: "warning" | "error" | "info";
    baslik: string;
    metin: string;
    kapatilabilir: boolean;
}

export interface ImzaKimligiState {
    socketBagli: boolean;
    devir: KimlikDevriDurumu;
    botKimligi: BotKimligi | null;
    lisans: ImzaLisansDurumu | null;
}

export const ESKI_KABUK_BASLIK = "İmzacı sürümü eski, yeniden kurun.";
export const ESKI_KABUK_METIN =
    "Bilgisayarınızdaki e-İmza servisi güvenlik güncellemelerini içermiyor. Güncel sürümü indirip yeniden kurunuz; " +
    "kurulana kadar bağlantı eski ve daha az güvenli yöntemle sürdürülür.";
export const LISANS_YOK_BASLIK = "Bu büroda etkin eİcraPro lisansı bulunamadı.";
export const TCKN_EKSIK_BASLIK = "Bu büronun eİcraPro lisansında TC kimlik numarası kayıtlı değil.";
export const TCKN_EKSIK_METIN = "UYAP girişi için destek ile iletişime geçin.";

export const DEVIR_HATA_METINLERI = {
    eksik: "Oturum bilgileriniz henüz yüklenmedi. Sayfayı yenileyip tekrar deneyin.",
    reddedildi: "İmzacı isteği kabul etmedi. İmzacıyı güncelleyip yeniden başlatın.",
    genel: "İmzacı bağlantısı yapılamadı. İmzacı programının açık olduğundan emin olun."
};

export const CANLI_BAGLANTI_YOK_METNI =
    "Kimlik imzacıya iletildi ancak canlı bağlantı kurulamadı. İmzacı programının açık olduğundan emin olun.";

/**
 * Socket'ten gelen ham botKimligi yükünü süzer. tenant_id boş / yok / null ya da yük
 * nesne değilse "kimlik yok" demektir (null). Kabuk <2.0.5 olayı hiç iletmez.
 */
export function botKimliginiNormallestir(ham: any): BotKimligi | null {
    if (!ham || typeof ham !== "object" || ham.tenant_id == null || ham.tenant_id === "") return null;
    return {
        tenantId: String(ham.tenant_id),
        buro: typeof ham.buro === "string" ? ham.buro : "",
        avukat: typeof ham.avukat === "string" ? ham.avukat : "",
        bekleyenGecis: ham.bekleyenGecis === true
    };
}

/**
 * GET /api/v1/imzaci/kimlik cevabını okur: {lisans_var, lisans_bitis, tckn, tckn_eksik,
 * lisans_sahibi}. Yalnız iki bayrak alınır (tckn / lisans sahibi tarayıcıya taşınmaz).
 * Sözleşmeye göre düz JSON; `data` zarfı da (savunma) kabul edilir. Tanınmayan cevap null.
 */
export function lisansCevabiniOku(cevap: any, tenantId: string): ImzaLisansDurumu | null {
    let govde = cevap;
    if (govde && typeof govde === "object" && govde.lisans_var === undefined && govde.data && typeof govde.data === "object") {
        govde = govde.data;
    }
    if (!govde || typeof govde !== "object" || typeof govde.lisans_var !== "boolean") return null;
    return {tenantId: tenantId, lisansVar: govde.lisans_var, tcknEksik: govde.tckn_eksik === true};
}

function devirHataMetni(sebep: any): string {
    if (sebep === "eksik") return DEVIR_HATA_METINLERI.eksik;
    if (sebep === "reddedildi" || sebep === "gecersiz") return DEVIR_HATA_METINLERI.reddedildi;
    return DEVIR_HATA_METINLERI.genel;
}

const ilkDevir = (): KimlikDevriDurumu => ({ok: false, eskiKabuk: false, kabukSurumu: null, sebep: null, neden: null, zaman: 0});
const ilkDurum = (): ImzaKimligiState => ({socketBagli: false, devir: ilkDevir(), botKimligi: null, lisans: null});

const mutations: MutationTree<ImzaKimligiState> = {
    setSocketBagli(state, bagli: boolean) {
        state.socketBagli = bagli === true;
    },
    setKimlikDevri(state, girdi: { sonuc: KimlikDevriSonucu, neden: KimlikDevriNedeni, zaman: number }) {
        state.devir = {
            ok: girdi.sonuc.ok === true,
            eskiKabuk: girdi.sonuc.eskiKabuk === true,
            kabukSurumu: girdi.sonuc.kabukSurumu || null,
            sebep: girdi.sonuc.ok ? null : (girdi.sonuc.sebep || "ag"),
            neden: girdi.neden,
            zaman: girdi.zaman
        };
    },
    setBotKimligi(state, ham: any) {
        state.botKimligi = botKimliginiNormallestir(ham);
    },
    setImzaLisansi(state, lisans: ImzaLisansDurumu | null) {
        state.lisans = lisans;
    }
};

const modulGetters: GetterTree<ImzaKimligiState, any> = {
    // Geliştirici modunda (mock veri) imzacı bağlı sayılır; mod kapanınca gerçek değer otomatik geri gelir.
    isImzaConnected: (state, _g, rootState) =>
        rootState.eicraproDevMode === true || (state.socketBagli && state.devir.ok),
    imzaEskiKabuk: (state) => state.devir.ok && state.devir.eskiKabuk,
    // Sunucudaki lisans gerçeği: null (bilinmiyor / sorun yok), 'lisans-yok' ya da 'tckn-eksik'.
    // Yalnız GEÇERLİ bürodan gelen cevap geçerli sayılır (büro değişince bayat uyarı kalmaz).
    imzaLisansDurumu: (state, _g, rootState) => {
        if (state.lisans === null || state.lisans.tenantId !== String(rootState.tenant_id)) return null;
        if (!state.lisans.lisansVar) return "lisans-yok";
        return state.lisans.tcknEksik ? "tckn-eksik" : null;
    },
    imzaUyarilari: (_s, g) => {
        const liste: ImzaUyarisi[] = [];
        // Kapatılamaz: imzacı bu durumda UYAP girişi yapmaz ve görev işlemez.
        if (g.imzaLisansDurumu === "lisans-yok") {
            liste.push({anahtar: "tckn-eksik", tip: "warning", baslik: LISANS_YOK_BASLIK, metin: "", kapatilabilir: false});
        } else if (g.imzaLisansDurumu === "tckn-eksik") {
            liste.push({anahtar: "tckn-eksik", tip: "warning", baslik: TCKN_EKSIK_BASLIK, metin: TCKN_EKSIK_METIN, kapatilabilir: false});
        }
        if (g.imzaEskiKabuk) {
            liste.push({anahtar: "eski-kabuk", tip: "warning", baslik: ESKI_KABUK_BASLIK, metin: ESKI_KABUK_METIN, kapatilabilir: true});
        }
        return liste;
    },
    botKimligi: (state) => state.botKimligi,
    botBuroFarkli: (state, _g, rootState) =>
        state.botKimligi !== null && !!rootState.tenant_id && state.botKimligi.tenantId !== String(rootState.tenant_id)
};

export function imzaKimligiModuluOlustur(): Module<ImzaKimligiState, any> {
    const yonetici = new KimlikDevriYoneticisi();
    // Birleşen / uçuşu paylaşan çağıranlar AYNI sonuç nesnesini alır; yan etkiler (state yazımı,
    // lisans sorgusu) devir başına bir kez yapılsın diye son işlenen sonuç tutulur.
    let sonIslenenSonuc: KimlikDevriSonucu | null = null;
    // Lisans sorgusunda son başlatılan kazanır (büro değişince eski cevap yazılmaz).
    let lisansIstekNo = 0;

    const actions: ActionTree<ImzaKimligiState, any> = {
        /**
         * Kimliği imzacıya devreder. Yaptığı tek şey `setApiCookiesToImza` (+ onun updateToken'ı):
         * heartbeat, kart listesi, UYAP KullaniciBilgileri, görev listesi YOK. Asla reddetmez.
         */
        async kimlikDevret({state, commit, dispatch, rootState}, payload?: { neden?: string }) {
            if (rootState.eicraproDevMode === true) return {ok: true, eskiKabuk: false, atlandi: true};
            const neden: KimlikDevriNedeni = nedenSec(payload && payload.neden);
            let sonuc: KimlikDevriSonucu;
            try {
                sonuc = await yonetici.calistir(neden, {
                    parmakIzi: () => parmakIziOlustur(rootState.accessToken, rootState.tenant_id, rootState.user),
                    socketBagli: () => state.socketBagli,
                    // @/main import EDİLMEZ (dairesel import): plugin prototipten alınır.
                    devret: () => Vue.prototype.$imza.setApiCookiesToImza()
                });
            } catch (e) {
                sonuc = devirHatasiniNormallestir(e);
            }
            if (!sonuc.atlandi && sonuc !== sonIslenenSonuc) {
                sonIslenenSonuc = sonuc;
                commit("setKimlikDevri", {sonuc: sonuc, neden: neden, zaman: Date.now()});
                // Lisans gerçeği beklenmez: devir sonucu (ve toast) sorgunun hızına bağlı olmasın.
                if (sonuc.ok) dispatch("imzaLisansKimliginiYukle").catch(() => undefined);
            }
            return sonuc;
        },
        /**
         * Büronun eİcraPro lisansı ve sahibinin TCKN'si sunucudan bir kez sorulur (sessiz: hata
         * toast'ı yok). 404 (eski backend) / ağ / 5xx: uyarı gösterilmez. Asla reddetmez.
         */
        async imzaLisansKimliginiYukle({commit, rootState}) {
            const tenantId = String(rootState.tenant_id == null ? "" : rootState.tenant_id);
            const istekNo = ++lisansIstekNo;
            let lisans: ImzaLisansDurumu | null = null;
            try {
                const cevap = await Vue.prototype.$http.get("/api/v1/imzaci/kimlik", {sessiz: true});
                lisans = lisansCevabiniOku(cevap, tenantId);
            } catch (e) {
                lisans = null;
            }
            if (istekNo === lisansIstekNo) commit("setImzaLisansi", lisans);
        },
        imzaSocketDurumu({commit}, bagli: boolean) {
            commit("setSocketBagli", bagli);
            if (!bagli) {
                yonetici.birlesmeyiSifirla();
                // Kopuşta imzacı/kabuk yok ve UYAP istekleri kabuğun 6081 proxy'sinden geçtiği için
                // atılamaz: imzacıdan gelen durum bayat kalmasın (eski oturum yoklamasının catch'inin
                // karşılığı). Modül namespaced değil, aşağıdaki mutation'lar kök store'a ulaşır.
                // Yeniden bağlanınca kabuk son değerleri yeniden gönderir.
                commit("setUyapConnection", false);
                commit("setBotKimligi", null);
                // Bot yok (imzacı/kabuk kapandı): botun engel bilgisi bayat kalmasın (DEV-3572). Panelin KENDİ kilidi kalır.
                commit("uyapEngeliImzaciTemizle");
                commit("setUyapAvukatBilgileri", undefined);
                commit("setUyapAvukatBakiye", undefined);
                commit("setUyapSorguBakiye", undefined);
            }
        },
        /** İmza simgesine tıklanınca: kimlik yeniden devredilir, sonuç kullanıcıya söylenir. */
        async connectToImza({dispatch, getters}) {
            const toast = Vue.prototype.$toast;
            const sonuc: KimlikDevriSonucu = await dispatch("kimlikDevret", {neden: "kullanici"});
            if (sonuc.ok) {
                if (getters.isImzaConnected) toast.success("İmzacı bağlantısı tamamlandı.");
                else toast.warning(CANLI_BAGLANTI_YOK_METNI);
            } else {
                toast.error(devirHataMetni(sonuc.sebep));
            }
        }
    };

    return {state: ilkDurum, getters: modulGetters, mutations: mutations, actions: actions};
}

export default imzaKimligiModuluOlustur();
